import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import { Calendar, Clock, User, Mail, Sparkles, CheckCircle2 } from 'lucide-react';
import { parseISO, addMinutes, isSameDay, format, setHours, setMinutes, startOfDay, addDays, isBefore } from 'date-fns';

export function BookingPage() {
  const { hostId } = useParams();
  const [brandName] = useState<string>('PlataPost');
  const [existingBookings, setExistingBookings] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(addDays(new Date(), 1)));
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  useEffect(() => {
    if (!hostId) return;
    const fetchHostAndBookings = async () => {
      try {
        const bookingsQuery = query(collection(db, 'bookings'), where('hostId', '==', hostId));
        const bookingDocs = await getDocs(bookingsQuery);
        const fetchedBookings = bookingDocs.docs.map(d => {
          const data = d.data();
          return {
            startTime: new Date(data.startTime),
            endTime: new Date(data.endTime)
          };
        });
        setExistingBookings(fetchedBookings);
      } catch (err: any) {
        console.error("Firebase Rules Error:", err.message);
      }
    };
    fetchHostAndBookings();
  }, [hostId]);

  const generateSlots = (date: Date) => {
    const slots = [];
    const now = new Date();

    // Check for South African timezone (Africa/Johannesburg)
    // For SA visitors, bookings only start from 1:00 PM (13:00)
    let startHour = 9;
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isSA = userTimeZone?.includes('Johannesburg') || userTimeZone?.includes('South_Africa') || (new Date().getTimezoneOffset() === -120);
      if (isSA) {
        startHour = 13;
      }
    } catch (e) {
      console.warn("Could not determine timezone, defaulting to 9am");
    }

    // Generate 15 minute slots between startHour and 5pm
    for (let h = startHour; h < 17; h++) {
      for (let m = 0; m < 60; m += 15) {
        const slotTime = setMinutes(setHours(date, h), m);
        if (isBefore(slotTime, now)) continue;
        
        // Check if overlaps with any existing booking
        const isBooked = existingBookings.some(b => {
          return slotTime.getTime() >= b.startTime.getTime() && slotTime.getTime() < b.endTime.getTime();
        });
        
        if (!isBooked) {
          slots.push(slotTime);
        }
      }
    }
    return slots;
  };

  const isSouthAfrica = () => {
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return userTimeZone?.includes('Johannesburg') || userTimeZone?.includes('South_Africa') || (new Date().getTimezoneOffset() === -120);
    } catch (e) {
      return false;
    }
  };

  const getAvailableDays = () => {
    const days = [];
    const today = startOfDay(new Date());
    for (let i = 1; i <= 14; i++) { // Next 14 days, skipping weekends for now if wanted
      const d = addDays(today, i);
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        days.push(d);
      }
    }
    return days;
  };

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostId || !selectedTime || !name || !email) return;
    
    setIsSubmitting(true);
    try {
        // Double check booking doesn't exist
        let bookingDocs;
        try {
          const bookingsQuery = query(collection(db, 'bookings'), where('hostId', '==', hostId));
          bookingDocs = await getDocs(bookingsQuery);
        } catch (getErr: any) {
          throw new Error("Failed to check existing bookings. This might be a database rules issue.");
        }
        const booked = bookingDocs.docs.some(d => {
            const data = d.data();
            const bStart = new Date(data.startTime).getTime();
            const sStart = selectedTime.getTime();
            return bStart === sStart;
        });

        if (booked) {
            alert('This slot was just booked by someone else. Please select another time.');
            // Refetch bookings to update UI
            const fetchedBookings = bookingDocs.docs.map(d => {
                const data = d.data();
                return {
                  startTime: new Date(data.startTime),
                  endTime: new Date(data.endTime)
                };
            });
            setExistingBookings(fetchedBookings);
            setSelectedTime(null);
            setIsSubmitting(false);
            return;
        }

      const endTime = addMinutes(selectedTime, 15);
      
      // Save public booking slot
      try {
        let bookingRef;
        try {
          bookingRef = await addDoc(collection(db, 'bookings'), {
            hostId,
            startTime: selectedTime.toISOString(),
            endTime: endTime.toISOString(),
            createdAt: new Date().toISOString()
          });
        } catch (addErr: any) {
          throw new Error("Could not save booking slot: " + addErr.message);
        }

        // Save PII to secure separate collection
      try {
        const docRef = doc(db, 'booking_guests', bookingRef.id);
        await setDoc(docRef, {
          hostId,
          guestName: name,
          guestEmail: email,
          bookingId: bookingRef.id
        });
      } catch (sgErr: any) {
        throw new Error("Could not save guest details: " + sgErr.message);
      }
        
        // Send email notification via Resend
        try {
          // Send to the primary user email
          const hostEmail = "dewaldjordaan5@gmail.com"; 
          await fetch('/api/send-booking-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              guestEmail: email,
              guestName: name,
              hostEmail,
              hostName: brandName,
              bookingTime: format(selectedTime, "EEEE, MMMM d, yyyy 'at' h:mm a")
            })
          });
        } catch (emailErr) {
          console.error("Failed to send email notification:", emailErr);
          // Let the success proceed anyway
        }
        
        setIsSuccess(true);
      } catch (err: any) {
        console.error("Booking failed details:", err);
        alert("Booking failed: " + err.message);
      }
    } catch (err: any) {
      console.error("Outer error details:", err);
      alert("Error: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center p-4 py-20">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
          <p className="text-gray-600 mb-4">
            Your 15-minute meeting with {brandName} has been booked for<br/>
            <span className="font-bold text-gray-900 text-lg">{selectedTime && format(selectedTime, "EEEE, MMMM d, yyyy 'at' h:mm a")}</span>
          </p>
          <div className="bg-indigo-50 text-indigo-700 text-sm p-4 rounded-xl mb-6">
            You will receive an email shortly confirming the details and your meeting link.
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="text-indigo-600 font-bold hover:text-indigo-700 transition"
          >
            Book another meeting
          </button>
        </div>
      </div>
    );
  }

  const availableDays = getAvailableDays();
  const availableSlots = selectedDate ? generateSlots(selectedDate) : [];

  return (
    <div className="min-h-screen bg-[#030303] p-6 lg:p-12 flex justify-center items-center">
      <div className="absolute inset-0 bg-indigo-600/5 blur-[120px] pointer-events-none" />
      
      <div className="max-w-5xl w-full glass-card rounded-[3.5rem] border border-white/10 shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[650px] relative z-10">
        
        {/* Left Side: Brand Info */}
        <div className="w-full md:w-2/5 p-10 lg:p-14 border-b md:border-b-0 md:border-r border-white/5 bg-white/[0.02] flex flex-col relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-500/10 blur-[80px] -ml-32 -mt-32" />
          
          <Link to="/" className="flex items-center gap-3 text-[10px] font-black text-gray-500 hover:text-indigo-400 transition-all uppercase tracking-widest mb-12 group relative z-10">
            <Sparkles className="w-4 h-4 group-hover:scale-125 transition-transform" />
            Back to PlataPost
          </Link>

          <div className="relative z-10 space-y-8 flex-grow">
            <div className="space-y-2">
              <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Schedule Meeting</p>
              <h1 className="text-4xl font-black text-white tracking-tight">{brandName}</h1>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/5 group-hover:scale-110 transition-transform">
                  <Clock className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-0.5">Duration</p>
                  <p className="text-white font-bold">15 Minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/5 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-0.5">Topic</p>
                  <p className="text-white font-bold leading-relaxed">Quick intro or strategic platform demo</p>
                </div>
              </div>
            </div>
          </div>
          
          {selectedTime && (
            <div className="bg-indigo-600/10 p-8 rounded-[2rem] border border-indigo-500/20 relative z-10 mt-12 group">
              <div className="absolute inset-0 bg-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4">Confirmed Selection</p>
              <div className="space-y-1">
                <p className="text-xl font-black text-white tracking-tight">{format(selectedTime, 'EEEE, MMMM d')}</p>
                <p className="text-lg font-bold text-gray-400">{format(selectedTime, 'h:mm a')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Calendar & Form */}
        <div className="w-full md:w-3/5 p-10 lg:p-14 bg-black/20">
          {!selectedTime ? (
            <div className="space-y-12">
              <div className="space-y-2">
                <h3 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-indigo-400" />
                  Select Date & Time
                </h3>
                <p className="text-gray-500 font-medium ml-9">
                  Pick a slot that works for your schedule.
                  {isSouthAfrica() && <span className="block text-indigo-400/80 text-xs mt-1 font-bold">Note: Afternoon slots only for South African visitors (1:00 PM local time onwards).</span>}
                </p>
              </div>
              
              <div className="space-y-12">
                {/* Dates */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-gray-600 uppercase tracking-[0.2em] ml-1">Available Days</h4>
                  <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
                    {availableDays.map(d => {
                      const isSelected = selectedDate && isSameDay(selectedDate, d);
                      return (
                        <button
                          key={d.toISOString()}
                          onClick={() => setSelectedDate(d)}
                          className={`flex-shrink-0 w-20 h-24 rounded-[1.5rem] flex flex-col items-center justify-center transition-all border ${
                            isSelected 
                              ? 'bg-indigo-600 border-indigo-500 text-white shadow-xl shadow-indigo-600/20 scale-105' 
                              : 'bg-white/5 border-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <span className="text-[10px] uppercase font-black tracking-widest mb-1 opacity-60">{format(d, 'MMM')}</span>
                          <span className="text-2xl font-black">{format(d, 'd')}</span>
                          <span className="text-[9px] font-black uppercase tracking-widest mt-1 opacity-50">{format(d, 'EEE')}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Times */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-gray-600 uppercase tracking-[0.2em] ml-1">Available Slots ({format(selectedDate, 'MMM d')})</h4>
                  {selectedDate && availableSlots.length === 0 ? (
                    <div className="p-8 text-center text-sm font-bold text-gray-500 glass-card rounded-[2rem] border border-white/5 border-dashed">No slots available for this day.</div>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableSlots.map(time => (
                        <button
                          key={time.toISOString()}
                          onClick={() => setSelectedTime(time)}
                          className="px-6 py-4 rounded-2xl bg-white/5 border border-white/5 text-sm font-black text-gray-300 hover:bg-indigo-600 hover:border-indigo-500 hover:text-white transition-all text-center tracking-tight"
                        >
                          {format(time, 'h:mm a')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                    <User className="w-6 h-6 text-indigo-400" />
                    Your Details
                  </h3>
                  <p className="text-gray-500 font-medium ml-9">All set! We just need a few basic details.</p>
                </div>
                <button 
                  type="button"
                  onClick={() => setSelectedTime(null)}
                  className="px-4 py-2 bg-white/5 text-[10px] font-black text-gray-500 uppercase tracking-widest rounded-xl hover:bg-white/10 hover:text-white transition-all border border-white/5"
                >
                  Change Time
                </button>
              </div>
              
              <form onSubmit={handleBook} className="space-y-8">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 leading-none">Full Name</label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full px-6 py-5 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all placeholder:text-gray-800 text-white font-black text-lg tracking-tight"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 leading-none">Email Address</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full px-6 py-5 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all placeholder:text-gray-800 text-white font-black text-lg tracking-tight"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white font-black py-6 rounded-2xl mt-4 hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30 disabled:opacity-50 uppercase tracking-[0.25em] text-xs border border-indigo-500"
                >
                  {isSubmitting ? 'Confirming...' : 'Confirm Demo Booking'}
                </button>

                <p className="text-center text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                  By confirming, you agree to our terms and privacy policy.
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
